import type { ReactNode } from 'react';
import { count, pct } from '../format';
import { Icon, type IconName } from './Icon';

export interface PageAction {
  label: string;
  icon?: IconName;
  primary?: boolean;
  title?: string;
  onClick?: () => void;
}

export function PageHeader({ icon, title, desc, actions }: { icon: IconName; title: string; desc: string; actions?: PageAction[] }) {
  return (
    <div className="ph">
      <div className="ph-l">
        <div className="mod-icon">
          <Icon name={icon} />
        </div>
        <div>
          <h1>{title}</h1>
          <p>{desc}</p>
        </div>
      </div>
      {actions?.length ? (
        <div className="actions">
          {actions.map((a) => (
            <button key={a.label} type="button" className={`btn${a.primary ? ' primary' : ''}`} title={a.title ?? a.label} onClick={a.onClick}>
              {a.icon ? <Icon name={a.icon} /> : null}
              {a.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export type Tone = 'brand' | 'ok' | 'warn' | 'bad' | 'violet' | 'teal' | 'org';

export function Stat({ icon, tone, value, label }: { icon: IconName; tone: Tone; value: ReactNode; label: string }) {
  return (
    <div className="stat">
      <div className={`stat-ic tone-${tone}`}>
        <Icon name={icon} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="stat-v num">{value}</div>
        <div className="stat-l">{label}</div>
      </div>
    </div>
  );
}

/**
 * CampusTrack staff-dashboard tile. Facts show a denominator and a bar; warnings show the share
 * of the total and a "Fix" affordance. The tile filters the grid below when pressed.
 */
export function KpiTile(props: {
  label: string;
  value: number;
  total?: number;
  icon: IconName;
  tone: Tone;
  warning?: boolean;
  bar?: 'ok' | 'violet' | 'teal' | 'brand';
  fixLabel?: string;
  pressed: boolean;
  onPress: () => void;
}) {
  const share = props.total ? pct(props.value, props.total) : 0;
  return (
    <button type="button" className="kpi" aria-pressed={props.pressed} onClick={props.onPress}>
      <div className="kpi-top">
        <div style={{ minWidth: 0 }}>
          <div className={`kpi-v num c-${props.tone}`}>
            {count(props.value)}
            {props.total !== undefined ? <small> / {count(props.total)}</small> : null}
          </div>
          <div className={`kpi-l${props.warning ? ` c-${props.tone}` : ''}`}>{props.label}</div>
        </div>
        <div className={`kpi-ic tone-${props.tone}`}>
          <Icon name={props.icon} />
        </div>
      </div>
      {props.warning ? (
        <div className="kpi-f">
          <span className={`c-${props.tone}`}>{share}%</span>
          <span className={`c-${props.tone}`}>{props.fixLabel} ›</span>
        </div>
      ) : props.bar ? (
        <div className="kpi-bar">
          <i className={`b-${props.bar}`} style={{ width: `${share}%` }} />
        </div>
      ) : null}
    </button>
  );
}

export function Badge({ tone, children }: { tone: 'ok' | 'bad' | 'warn' | 'info' | 'neutral' | 'violet' | 'teal'; children: ReactNode }) {
  return <span className={`badge bd-${tone}`}>{children}</span>;
}
